// src/app/admin/dashboard/page.tsx
'use client';

import { useEffect, useRef, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';

interface KPI { title: string; value: string; trend: string; isPositive: boolean; }
interface AgencyPin { id: string; name: string; lat: number; lng: number; radius: number; fillKey: string; ventas: number; premios: number; }
interface Alerta { tipo: string; agencia: string; mensaje: string; color: 'rose' | 'amber' | 'gray'; }

export default function AdminDashboardPage() {
  const mapContainer = useRef<HTMLDivElement>(null);
  
  const [mapScriptsLoaded, setMapScriptsLoaded] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [filtroZona, setFiltroZona] = useState('ALL');

  const [kpis, setKpis] = useState<KPI[]>([
    { title: 'Ingresos Diarios', value: '$0.00', trend: 'Hoy', isPositive: true },
    { title: 'Premios Pagados', value: '$0.00', trend: 'Hoy', isPositive: true },
    { title: 'Volumen de Transacciones', value: '0', trend: 'Hoy', isPositive: true },
    { title: 'Egresos Operativos', value: '$0.00', trend: 'Hoy', isPositive: false }
  ]);
  const [agencias, setAgencias] = useState<AgencyPin[]>([]);
  const [alertas, setAlertas] = useState<Alerta[]>([]);

  // 1. CARGAR DATOS REALES DEL BACKEND
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const res = await fetch('/api/admin/dashboard', {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          }
        });
        
        if (res.ok) {
          const data = await res.json();
          if(data.kpis) setKpis(data.kpis);
          if(data.agencias) setAgencias(data.agencias);
          if(data.alertas) setAlertas(data.alertas);
        }
      } catch (error) {
        console.error("Error obteniendo datos del dashboard:", error);
      } finally {
        setIsFetchingData(false);
      }
    };

    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 60000);
    return () => clearInterval(interval);
  }, []);

  // 2. CARGADOR SECUENCIAL DE DEPENDENCIAS
  useEffect(() => {
    const loadScript = (src: string) => {
      return new Promise<void>((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve();
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Error cargando ${src}`));
        document.body.appendChild(script);
      });
    };

    const loadDependencies = async () => {
      try {
        await loadScript("https://cdnjs.cloudflare.com/ajax/libs/d3/3.5.3/d3.min.js");
        await loadScript("https://cdnjs.cloudflare.com/ajax/libs/topojson/1.6.9/topojson.min.js");
        await loadScript("https://cdnjs.cloudflare.com/ajax/libs/datamaps/0.5.9/datamaps.all.min.js");
        setMapScriptsLoaded(true);
      } catch (error) {
        console.error("Fallo al cargar librerías del mapa");
      }
    };
    loadDependencies();
  }, []);

  // 3. MAPA CON AUTO-ENCUADRE Y TOOLTIP ANTI-APLASTAMIENTO
  useEffect(() => {
    if (mapScriptsLoaded && !isFetchingData && mapContainer.current && window.Datamap) {
      mapContainer.current.innerHTML = ''; 

      const burbujasDatamaps = agencias.map(ag => ({
        ...ag,
        latitude: ag.lat,
        longitude: ag.lng,
        radius: 12, 
        fillKey: ag.fillKey || 'ALERTA' 
      }));

      new window.Datamap({
        element: mapContainer.current,
        scope: 'world',
        geographyConfig: {
          popupOnHover: false,
          highlightOnHover: false,
          borderColor: '#cbd5e1', 
          borderWidth: 1
        },
        fills: { 
          defaultFill: '#f1f5f9', 
          'ALTO_RENDIMIENTO': '#10b981', 
          'ESTABLE': '#3b82f6', 
          'ALERTA': '#ef4444' 
        },
        setProjection: function(element: any) {
          let center: [number, number] = [-78.5, -1.8]; 
          let scale = 3500; 

          if (burbujasDatamaps.length > 0) {
            const lats = burbujasDatamaps.map(b => b.latitude);
            const lngs = burbujasDatamaps.map(b => b.longitude);
            const minLat = Math.min(...lats);
            const maxLat = Math.max(...lats);
            const minLng = Math.min(...lngs);
            const maxLng = Math.max(...lngs);

            center = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];

            const latDiff = maxLat - minLat;
            const lngDiff = maxLng - minLng;
            const maxDiff = Math.max(latDiff, lngDiff);

            if (burbujasDatamaps.length === 1 || maxDiff < 0.1) {
              scale = 45000; 
            } else {
              scale = Math.min(45000, Math.max(3500, 6000 / maxDiff)); 
            }
          }

          const projection = window.d3.geo.mercator()
            .center(center) 
            .scale(scale) 
            .translate([element.offsetWidth / 2, element.offsetHeight / 2]);
          const path = window.d3.geo.path().projection(projection);
          return { path: path, projection: projection };
        },
        done: function(datamap: any) {
          if (burbujasDatamaps.length > 0) {
            datamap.bubbles(burbujasDatamaps, {
              popupTemplate: function(geo: any, data: any) {
                const dotColor = data.fillKey === 'ALTO_RENDIMIENTO' ? '#10b981' : data.fillKey === 'ESTABLE' ? '#3b82f6' : '#ef4444';
                
                // TOOLTIP BLINDADO: white-space: nowrap y min-width fuerzan formato horizontal
                return `
                  <div style="background: white; padding: 16px; border-radius: 12px; box-shadow: 0 10px 25px -5px rgba(0,0,0,0.2); border: 1px solid #e2e8f0; font-family: sans-serif; min-width: 240px; white-space: nowrap;">
                    <div style="border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; margin-bottom: 12px; display: flex; align-items: center; gap: 8px;">
                      <div style="width: 12px; height: 12px; border-radius: 50%; background-color: ${dotColor}; flex-shrink: 0;"></div>
                      <strong style="color: #0f172a; font-size: 14px; text-transform: uppercase; letter-spacing: 0.5px;">${data.name}</strong>
                    </div>
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; font-size: 13px;">
                      <span style="color: #64748b; font-weight: 600;">Ingresos</span> 
                      <span style="color: #059669; font-weight: 900; font-size: 14px;">$${Number(data.ventas).toFixed(2)}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; align-items: center; font-size: 13px;">
                      <span style="color: #64748b; font-weight: 600;">Premios</span> 
                      <span style="color: #e11d48; font-weight: 900; font-size: 14px;">$${Number(data.premios).toFixed(2)}</span>
                    </div>
                  </div>`;
              }
            });
          }
        }
      });
    }
  }, [mapScriptsLoaded, isFetchingData, agencias, filtroZona]);

  return (
    <DashboardLayout title="Centro de Comando Nacional">
      
      <style dangerouslySetInnerHTML={{__html: `
        .datamaps-subunit { fill: #f1f5f9 !important; stroke: #cbd5e1 !important; stroke-width: 1px !important; }
        .datamaps-bubble { 
          stroke: #ffffff !important; 
          stroke-width: 3px !important; 
          fill-opacity: 1 !important; 
          cursor: pointer;
          transition: stroke-width 0.2s;
        }
        .datamaps-bubble:hover { stroke-width: 5px !important; }
        
        /* CORRECCIÓN CRÍTICA: Resetea el contenedor por defecto de Datamaps para evitar aplastamiento */
        .hoverinfo {
          padding: 0 !important;
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }
      `}} />

      <div className="max-w-7xl mx-auto space-y-6">
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {isFetchingData ? (
            <div className="col-span-4 p-8 text-center text-gray-400 font-bold animate-pulse">Consultando telemetría en tiempo real...</div>
          ) : (
            kpis.map((kpi, idx) => (
              <div key={idx} className="bg-white dark:bg-gray-800 p-5 rounded-3xl border border-gray-200 dark:border-gray-700 shadow-sm transition-transform hover:scale-105">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{kpi.title}</p>
                <div className="flex items-end gap-3">
                  <p className="text-3xl font-black text-gray-800 dark:text-white">{kpi.value}</p>
                  <span className={`text-sm font-bold pb-1 ${kpi.isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
                    {kpi.trend}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          
          <div className="xl:col-span-3 bg-white dark:bg-gray-800 rounded-3xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 relative flex flex-col h-[600px]">
            <div className="flex justify-between items-center mb-4 z-10">
              <h2 className="text-xl font-black text-gray-800 dark:text-white">Rendimiento Geográfico</h2>
              <select value={filtroZona} onChange={(e) => setFiltroZona(e.target.value)} className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-sm font-bold text-gray-700 dark:text-gray-300 rounded-xl px-4 py-2 outline-none cursor-pointer">
                <option value="ALL">🌐 Todo el País</option>
                <option value="MANABI">📍 Zona Manabí</option>
                <option value="GUAYAS">📍 Zona Guayas</option>
              </select>
            </div>

            <div className="flex-1 w-full relative bg-[#f8fafc] dark:bg-gray-900/50 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800">
               {(!mapScriptsLoaded || isFetchingData) && (
                 <div className="absolute inset-0 flex items-center justify-center">
                   <span className="text-gray-400 font-bold animate-pulse">Renderizando cartografía...</span>
                 </div>
               )}
               <div ref={mapContainer} className="absolute inset-0"></div>
            </div>
            
            <div className="flex gap-4 justify-center mt-4 z-10">
               <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-emerald-500 shadow-sm"></div><span className="text-xs font-bold text-gray-500 uppercase">Supera Meta</span></div>
               <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-blue-500 shadow-sm"></div><span className="text-xs font-bold text-gray-500 uppercase">Estable</span></div>
               <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-rose-500 shadow-sm"></div><span className="text-xs font-bold text-gray-500 uppercase">Riesgo / Inactivo</span></div>
            </div>
          </div>

          <div className="xl:col-span-1 space-y-6">
            <div className="bg-rose-50 dark:bg-rose-900/10 rounded-3xl border border-rose-200 dark:border-rose-800 p-6 h-full overflow-y-auto">
              <h2 className="text-lg font-black text-rose-700 dark:text-rose-400 mb-4 flex items-center gap-2">⚠️ Alertas Activas</h2>
              
              <div className="space-y-3">
                {alertas.length === 0 && !isFetchingData ? (
                  <p className="text-sm text-gray-500 font-medium">Todo está operando dentro de los parámetros normales.</p>
                ) : (
                  alertas.map((alerta, idx) => {
                    const colorMap = {
                      rose: 'bg-rose-500 border-rose-100 text-rose-500',
                      amber: 'bg-amber-500 border-amber-100 text-amber-500',
                      gray: 'bg-gray-500 border-gray-200 text-gray-600'
                    };
                    const cls = colorMap[alerta.color];
                    
                    return (
                      <div key={idx} className={`bg-white dark:bg-gray-800 p-4 rounded-2xl border shadow-sm relative overflow-hidden ${cls.split(' ')[1]}`}>
                        <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${cls.split(' ')[0]}`}></div>
                        <p className={`text-xs font-bold mb-1 ${cls.split(' ')[2]}`}>{alerta.tipo}</p>
                        <p className="font-black text-gray-800 dark:text-gray-200 text-sm">{alerta.agencia}</p>
                        <p className="text-xs text-gray-500 mt-1">{alerta.mensaje}</p>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </DashboardLayout>
  );
}

declare global {
  interface Window { Datamap: any; d3: any; }
}